/**
 * Public landing / coming-soon splash for `/`.
 *
 * Audience: anyone landing on forgeledger.net before they've signed up.
 * Tone: confident & UK-warm — short sentences, plain English, no jargon.
 *
 * CTAs:
 *   - Primary  → "Create free account" (/register)
 *   - Secondary → "Get launch updates" — drops their email into the
 *     existing `/api/waitlist` endpoint (source = "landing-page").
 *
 * Designed to be swapped to a fuller marketing landing the week of
 * launch — the structure (hero / what it does / trust / footer) gives
 * us slots to expand without a rewrite.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowRight, ShieldCheck, TrendingUp, Calendar, AlertTriangle, Check, Flame } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Seo from '../components/Seo';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_budget-forecast-27/artifacts/w1b6b3hu_mini.png';

const HIGHLIGHTS = [
  {
    icon: TrendingUp,
    title: 'See your balance, weeks ahead',
    body: 'Plain-maths projection from your recurring bills and income. No black-box AI — just numbers you can check.',
  },
  {
    icon: Calendar,
    title: 'Reconcile against your real statement',
    body: "Tick off each transaction as it lands. Spot the bank's mistakes — or your own — before they snowball.",
  },
  {
    icon: AlertTriangle,
    title: 'Shortfall warnings that mean something',
    body: 'We tell you the exact day a balance dips below zero — and the bill that pushed it over. Time to act, not panic.',
  },
];

const ComingSoon = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [signupsOpen, setSignupsOpen] = useState(false);
  const [founding, setFounding] = useState(null);

  useEffect(() => {
    axios.get(`${API}/feature-flags`)
      .then((r) => setSignupsOpen(!!r.data?.public_signups_enabled))
      .catch(() => setSignupsOpen(false));  // fail closed — hard gate stays closed
    axios.get(`${API}/coupons/launch/availability`)
      .then((r) => setFounding(r.data))
      .catch(() => setFounding(null));  // counter stays hidden if endpoint fails
  }, []);

  const joinWaitlist = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, {
        email: email.trim(),
        tier: 'general',
        source: 'landing-page',
      });
      setSubscribed(true);
      toast.success("You're on the list — we'll be in touch.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't add you to the list — try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#020617]" data-testid="coming-soon-page">
      <Seo
        title="Forge Ledger — See your future balance. Catch shortfalls early."
        description="UK-built personal finance: future balance forecasts from your recurring bills, real-statement reconciliation, and shortfall warnings you can act on. GBP, DD/MM/YYYY, GDPR-safe. Free Ember tier."
        path="/"
      />

      {/* Header */}
      <header className="border-b border-[#E2E8F0] bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5" data-testid="coming-soon-brand">
            <img
              src={LOGO_URL}
              alt="Forge Ledger"
              className="w-9 h-9 rounded-lg object-cover ring-1 ring-[#E2E8F0]"
            />
            <span className="font-bold tracking-tight text-lg">Forge Ledger</span>
          </div>
          <nav className="flex items-center gap-3">
            <Link
              to="/security"
              className="hidden sm:inline-flex text-sm font-medium text-[#64748B] hover:text-[#020617] transition-colors"
              data-testid="coming-soon-nav-security"
            >
              Security
            </Link>
            <Link
              to="/login"
              className="text-sm font-medium text-[#020617] hover:text-[#0052FF] transition-colors"
              data-testid="coming-soon-nav-login"
            >
              Sign in
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 sm:px-8 pt-16 sm:pt-24 pb-12">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EFF6FF] border border-[#BFDBFE] mb-6" data-testid="coming-soon-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-[#0052FF] animate-pulse" />
            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-[#1E40AF]">
              Built in Britain · {signupsOpen ? 'Now live' : 'Launching soon'}
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05] mb-6">
            See your future balance.<br />
            <span className="text-[#0052FF]">Catch shortfalls early.</span>
          </h1>

          <p className="text-lg sm:text-xl text-[#475569] leading-relaxed mb-10 max-w-2xl">
            Forge Ledger is a personal finance tool for people who want to <strong>actually know</strong> where their money will be next month. Recurring bills, real-statement reconciliation, and a six-month forecast — in plain English, in pounds, in the date format that makes sense.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mb-4" data-testid="coming-soon-primary-cta-row">
            {signupsOpen ? (
              <Link to="/register">
                <Button
                  className="bg-[#0052FF] hover:bg-[#003ECC] text-white font-semibold px-6 py-6 text-base rounded-lg w-full sm:w-auto"
                  data-testid="coming-soon-register-cta"
                >
                  Create free account
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            ) : (
              <a href="#waitlist">
                <Button
                  className="bg-[#0052FF] hover:bg-[#003ECC] text-white font-semibold px-6 py-6 text-base rounded-lg w-full sm:w-auto"
                  data-testid="coming-soon-waitlist-jump-cta"
                >
                  Join the launch list
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            )}
            <Link to="/security">
              <Button
                variant="outline"
                className="border-[#E2E8F0] text-[#020617] hover:bg-white px-6 py-6 text-base rounded-lg w-full sm:w-auto"
                data-testid="coming-soon-security-cta"
              >
                <ShieldCheck className="w-4 h-4 mr-2 text-[#0052FF]" />
                How your data stays safe
              </Button>
            </Link>
          </div>

          <p className="text-xs text-[#94A3B8]">
            {signupsOpen
              ? 'Ember tier is free, forever. No card required.'
              : "We're hand-finishing the launch. Drop your email below and we'll let you in first."}
          </p>
        </div>
      </section>

      {/* What it does */}
      <section className="max-w-6xl mx-auto px-6 sm:px-8 py-16">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2">What it actually does</h2>
        <p className="text-[#64748B] mb-10 max-w-2xl">No fluff. Three things, done properly.</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="coming-soon-highlights">
          {HIGHLIGHTS.map(({ icon: Icon, title, body }, i) => (
            <div
              key={title}
              className="bg-white border border-[#E2E8F0] rounded-xl p-6 hover:border-[#CBD5E1] hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all"
              data-testid={`coming-soon-highlight-${i}`}
            >
              <div className="w-10 h-10 rounded-lg bg-[#EFF6FF] flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-[#0052FF]" />
              </div>
              <h3 className="text-lg font-bold mb-2">{title}</h3>
              <p className="text-sm text-[#64748B] leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Founding spots — three tiers, live counter from /api/coupons/launch/availability */}
      {founding && (
        <section className="max-w-6xl mx-auto px-6 sm:px-8 pb-8" data-testid="coming-soon-founding-spots">
          <div className="bg-gradient-to-br from-[#FFFBEB] to-[#FEF3C7] border border-[#FDE68A] rounded-2xl p-6 sm:p-8">
            <div className="flex items-start gap-3 mb-5">
              <div className="w-10 h-10 rounded-lg bg-white/70 flex items-center justify-center shrink-0">
                <Flame className="w-5 h-5 text-[#F97316]" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-[#020617]">
                  Founding member offer — first 100 per tier
                </h2>
                <p className="text-sm text-[#78350F] mt-1">
                  Save <strong>£{Number(founding.monthly_discount_gbp).toFixed(0)}/month for 6 months</strong> on monthly billing, or
                  <strong> £{Number(founding.annual_discount_gbp).toFixed(0)} off your first year</strong> on annual. Applied automatically at checkout while spots last.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { key: 'steel', label: 'Steel', price: '£4/mo' },
                { key: 'damascus', label: 'Damascus', price: '£7/mo' },
                { key: 'titanium', label: 'Titanium', price: '£10/mo · waitlist' },
              ].map(({ key, label, price }) => {
                const tier = founding[key] || { remaining: founding.cap_per_tier, redeemed: 0 };
                const cap = founding.cap_per_tier || 100;
                const pct = Math.min(100, Math.max(0, ((cap - tier.remaining) / cap) * 100));
                const exhausted = tier.remaining <= 0;
                const copy = exhausted
                  ? 'Founding offer ended'
                  : tier.remaining >= 90
                    ? `${cap} founding spots open`
                    : tier.remaining >= 30
                      ? `${tier.remaining} of ${cap} spots left`
                      : `Almost gone — ${tier.remaining} spot${tier.remaining === 1 ? '' : 's'} left`;
                return (
                  <div
                    key={key}
                    className="bg-white/85 backdrop-blur-sm border border-white rounded-xl p-4"
                    data-testid={`coming-soon-founding-${key}`}
                  >
                    <div className="flex items-baseline justify-between mb-2">
                      <span className="font-bold text-[#020617]">{label}</span>
                      <span className="text-xs text-[#64748B]">{price}</span>
                    </div>
                    <div className="text-xs uppercase tracking-[0.12em] font-bold text-[#020617] mb-2">
                      {copy}
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-[#FEF3C7] overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-[#F97316] to-[#EA580C] transition-all duration-300"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Waitlist */}
      <section id="waitlist" className="max-w-6xl mx-auto px-6 sm:px-8 py-16 scroll-mt-16">
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-8 sm:p-12 max-w-3xl">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3">
            {signupsOpen
              ? 'Not ready to sign up? Leave your email.'
              : 'Get in first when we open the doors.'}
          </h2>
          <p className="text-[#64748B] mb-6 max-w-xl">
            {signupsOpen
              ? "We'll send you one note when the next tier (Steel — full forecasting + categories) opens to the public. No drip campaigns, no nonsense. Unsubscribe with one click."
              : "We're polishing the final corners before public signups open. Drop your email and we'll let you in the moment we go live — and you'll lock in a launch-week discount when Steel opens."}
          </p>

          {subscribed ? (
            <div
              className="inline-flex items-center gap-2 px-4 py-3 rounded-lg bg-[#ECFDF5] border border-[#A7F3D0] text-[#065F46] text-sm font-medium"
              data-testid="coming-soon-waitlist-confirmation"
            >
              <Check className="w-4 h-4" />
              You&apos;re on the list. Talk soon.
            </div>
          ) : (
            <form onSubmit={joinWaitlist} className="flex flex-col sm:flex-row gap-3 max-w-md" data-testid="coming-soon-waitlist-form">
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="flex-1 bg-[#F8FAFC] border border-[#E2E8F0] focus:border-[#0052FF] focus:ring-1 focus:ring-[#0052FF] rounded-lg px-4 py-3 text-base"
                disabled={submitting}
                data-testid="coming-soon-waitlist-email-input"
              />
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#020617] hover:bg-[#1E293B] text-white font-semibold px-6 py-3 rounded-lg"
                data-testid="coming-soon-waitlist-submit"
              >
                {submitting ? 'Adding…' : 'Get launch updates'}
              </Button>
            </form>
          )}
        </div>
      </section>

      <Footer variant="full" />
    </div>
  );
};

export default ComingSoon;
